import { rawFetch } from "./rawFetch";
import { toAppContext, AppContext } from "./brandConfig";

// Every call here uses rawFetch: these ARE the refresh dance and must not recurse through the 401 interceptor.

// session_ended: the refresh token is dead and the cookies are gone. retry_later: Auth0 or the
// backend could not answer, the session is intact. csrf: the double-submit pair drifted.
export type RefreshFailureCode = "session_ended" | "retry_later" | "csrf";

export interface RefreshOutcome {
  ok: boolean;
  status?: number;
  code?: RefreshFailureCode;
  retryAfterMs?: number;
  brand?: AppContext;
  expiresIn?: number;
}

// Raised on window when a refresh answers session_ended; AuthContext ends the session on it.
export const SESSION_ENDED_EVENT = "asksmart:session-ended";

const FAILED: RefreshOutcome = { ok: false };

const backendUrl = () => process.env.REACT_APP_BACKEND_URL;

const FAILURE_CODES: ReadonlySet<string> = new Set(["session_ended", "retry_later", "csrf"]);

// A 401 from a refresh endpoint is session_ended even when the body says nothing (older backend).
async function failureOf(res: Response): Promise<RefreshOutcome> {
  const data = await res.json().catch(() => ({}));
  const code = FAILURE_CODES.has(data?.code)
    ? (data.code as RefreshFailureCode)
    : res.status === 401
      ? "session_ended"
      : undefined;
  const header = Number(res.headers?.get?.("retry-after"));
  const seconds =
    Number.isFinite(header) && header > 0
      ? header
      : typeof data?.retryAfter === "number"
        ? data.retryAfter
        : undefined;
  return {
    ok: false,
    status: res.status,
    code,
    retryAfterMs: seconds === undefined ? undefined : seconds * 1000,
  };
}

async function outcomeOf(res: Response): Promise<RefreshOutcome> {
  if (!res.ok) return failureOf(res);
  const data = await res.json().catch(() => ({}));
  return {
    ok: true,
    status: res.status,
    brand: toAppContext(data?.brand),
    expiresIn: typeof data?.expiresIn === "number" ? data.expiresIn : undefined,
  };
}

const registrableDomain = (hostname: string) => hostname.split(".").slice(-2).join(".");

// A branded custom domain (app.mytosp.com) is cross-site to the backend, where SameSite cookies can neither be sent nor stored.
export function isCrossSiteToBackend(hostname: string = window.location.hostname): boolean {
  const backend = process.env.REACT_APP_BACKEND_URL;
  if (!backend) return false;
  let backendHost: string;
  try {
    backendHost = new URL(backend).hostname;
  } catch {
    return false;
  }
  if (hostname === "localhost" || backendHost === "localhost") return false;
  // Two labels are enough for asksmart.ai, mytosp.com and caddiegames.com.
  return registrableDomain(hostname) !== registrableDomain(backendHost);
}

// ── Session expiry and backoff: what refresh-on-activity keys off ────────────
const TEN_MINUTES_MS = 10 * 60 * 1000;
const BACKOFF_BASE_MS = 60 * 1000;
const BACKOFF_CAP_MS = 15 * 60 * 1000;

let expiresAt: number | null = null;
let lifetimeMs: number | null = null;
let notBeforeAt = 0;
let failureCount = 0;
let lastFailure: RefreshOutcome | null = null;

export function noteSessionResponse(
  data: { sessionExpiresAt?: unknown; sessionIssuedAt?: unknown } | null | undefined,
): void {
  const exp = typeof data?.sessionExpiresAt === "number" ? data.sessionExpiresAt : null;
  const iat = typeof data?.sessionIssuedAt === "number" ? data.sessionIssuedAt : null;
  expiresAt = exp;
  if (exp !== null && iat !== null && exp > iat) lifetimeMs = exp - iat;
  notBeforeAt = 0;
  failureCount = 0;
  lastFailure = null;
}

// Exponential with jitter (half to full), capped, so a dead Auth0 is not hammered by every tab.
function backoffMs(): number {
  const exponential = Math.min(BACKOFF_CAP_MS, BACKOFF_BASE_MS * 2 ** failureCount);
  return exponential * (0.5 + Math.random() * 0.5);
}

function endSession(outcome: RefreshOutcome): void {
  clearSessionExpiry();
  csrfTokenCache = null;
  lastFailure = outcome;
  window.dispatchEvent(
    new CustomEvent(SESSION_ENDED_EVENT, { detail: { status: outcome.status } }),
  );
}

function noteRefreshOutcome(outcome: RefreshOutcome, now = Date.now()): void {
  if (outcome.ok) {
    if (typeof outcome.expiresIn === "number") {
      lifetimeMs = outcome.expiresIn * 1000;
      expiresAt = now + lifetimeMs;
    }
    notBeforeAt = 0;
    failureCount = 0;
    lastFailure = null;
    return;
  }
  if (outcome.code === "session_ended") {
    endSession(outcome);
    return;
  }
  lastFailure = outcome;
  notBeforeAt = now + (outcome.retryAfterMs ?? backoffMs());
  failureCount += 1;
}

export function clearSessionExpiry(): void {
  expiresAt = null;
  lifetimeMs = null;
  notBeforeAt = 0;
  failureCount = 0;
}

// Unknown expiry counts as due (one refresh teaches it); a transient failure backs off first.
export function isSessionRefreshDue(now = Date.now()): boolean {
  if (now < notBeforeAt) return false;
  if (expiresAt === null) return true;
  const margin =
    lifetimeMs !== null && lifetimeMs * 0.2 < TEN_MINUTES_MS ? lifetimeMs * 0.2 : TEN_MINUTES_MS;
  return expiresAt - now <= margin;
}

// The csrf_token cookie is httpOnly; the SPA learns its value from GET /csrf-token and keeps it in memory.
let csrfTokenCache: string | null = null;

async function fetchCsrfToken(): Promise<string | null> {
  try {
    const res = await rawFetch(`${backendUrl()}/csrf-token`, {
      credentials: "include",
      cache: "no-store",
    });
    if (!res.ok) return null;
    const data = await res.json().catch(() => ({}));
    return typeof data?.csrfToken === "string" && data.csrfToken ? data.csrfToken : null;
  } catch {
    return null;
  }
}

function postRefresh(csrf: string): Promise<Response> {
  return rawFetch(`${backendUrl()}/refresh`, {
    method: "POST",
    credentials: "include",
    headers: { "X-CSRF-Token": csrf },
  });
}

export async function trySilentRefresh(): Promise<RefreshOutcome> {
  try {
    const csrf = csrfTokenCache ?? (await fetchCsrfToken());
    if (!csrf) return FAILED;
    csrfTokenCache = csrf;

    let res = await postRefresh(csrf);
    if (res.status === 403) {
      // Cookie and cached token diverged (another tab, or a backend re-mint): refetch once, retry once.
      csrfTokenCache = null;
      const fresh = await fetchCsrfToken();
      if (!fresh) return failureOf(res);
      csrfTokenCache = fresh;
      res = await postRefresh(fresh);
    }
    return outcomeOf(res);
  } catch {
    return FAILED;
  }
}

// The live request path must call this, not trySilentRefresh: it records the outcome for backoff and session end.
// The httpOnly cookie is the only refresh credential on every platform; no token is ever handed to JavaScript.
export async function tryRefreshSession(): Promise<RefreshOutcome> {
  const outcome = await trySilentRefresh();
  noteRefreshOutcome(outcome);
  return outcome;
}

// Single-flight: two refreshes racing outside the Auth0 rotation overlap revoke the whole token family.
let refreshInFlight: Promise<RefreshOutcome> | null = null;

export function refreshSessionOnce(): Promise<RefreshOutcome> {
  // Still backing off from a transient failure: answer with it instead of calling again.
  if (lastFailure && Date.now() < notBeforeAt) return Promise.resolve(lastFailure);
  if (!refreshInFlight) {
    refreshInFlight = tryRefreshSession().finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}
